import { type AxiosResponse } from "axios";
import React, {
  createContext,
  type FC,
  useContext,
  useMemo,
  useReducer,
} from "react";

import { useCreateReport } from "@/api/reports";
import {
  type Account,
  type ChatMessage,
  type Group,
  type Status,
} from "@/schemas";
import { getActualStatus } from "@/utils/status";

interface ReportsProviderProps {
  children: React.ReactNode;
  entityType: ReportableEntities;
  account: Account;
  group?: Group;
  status?: Status;
  chatMessage?: ChatMessage;
}
enum ReportActionsKind {
  COMMENT_CHANGE = "COMMENT_CHANGE",
  BLOCK_CHANGE = "BLOCK_CHANGE",
  RULE_CHANGE = "RULE_CHANGE",
}

type ReportsContextType = {
  accountId: string | null;
  entityType: string;
  isBlocked: boolean;
  ruleIds: string[];
  selectedStatus: Status | null;
  selectedChatMessage: ChatMessage | null;
  selectedGroup: Group | null;
  comment: string;
  isSubmitting: boolean;
  changeReportBlock: (block: boolean) => void;
  changeReportComment: (comment: string) => void;
  changeReportRule: (rule_id: string) => void;
  submitReport: () => Promise<AxiosResponse<any, any>>;
};

const ReportsContext = createContext<ReportsContextType | undefined>(undefined);

enum ReportableEntities {
  ACCOUNT = "ACCOUNT",
  CHAT_MESSAGE = "CHAT_MESSAGE",
  GROUP = "GROUP",
  STATUS = "STATUS",
}

type reportStateType = {
  entityType: ReportableEntities;
  account_id: string | null;
  status: Status | null;
  chat_message: ChatMessage | null;
  group: Group | null;
  comment: string;
  forward: boolean;
  block: boolean;
  rule_ids: string[];
};

const initialState: reportStateType = {
  entityType: ReportableEntities.ACCOUNT,
  account_id: null,
  status: null,
  chat_message: null,
  group: null,
  comment: "",
  forward: false,
  block: false,
  rule_ids: [],
};

type ReportsActionType = {
  type: ReportActionsKind;
  payload?: any;
};

const reportsReducer = (
  state: reportStateType,
  action: ReportsActionType,
): reportStateType => {
  const { type, payload } = action;
  switch (type) {
    case ReportActionsKind.COMMENT_CHANGE:
      return {
        ...state,
        comment: payload.comment,
      };
    case ReportActionsKind.BLOCK_CHANGE:
      return {
        ...state,
        block: payload.block,
      };
    case ReportActionsKind.RULE_CHANGE:
      return {
        ...state,
        rule_ids: state.rule_ids.includes(payload.rule_id)
          ? state.rule_ids.filter((id) => id !== payload.rule_id)
          : [...state.rule_ids, payload.rule_id],
      };
    default:
      return state;
  }
};

const ReportsProvider: FC<ReportsProviderProps> = ({
  children,
  entityType,
  account,
  group,
  status,
  chatMessage,
}) => {
  const { mutateAsync, isLoading: isSubmitting } = useCreateReport();

  const initialStateWithProps = {
    ...initialState,
    entityType,
    account_id: account.id,
    chat_message: chatMessage || null,
    group: group || null,
    status: getActualStatus(status),
  };

  const [state, dispatch] = useReducer(reportsReducer, initialStateWithProps);

  const changeReportBlock = (block: boolean) => {
    dispatch({
      type: ReportActionsKind.BLOCK_CHANGE,
      payload: { block },
    });
  };

  const changeReportComment = (comment: string) => {
    dispatch({
      type: ReportActionsKind.COMMENT_CHANGE,
      payload: { comment },
    });
  };

  const changeReportRule = (rule_id: string) => {
    dispatch({
      type: ReportActionsKind.RULE_CHANGE,
      payload: { rule_id },
    });
  };

  const submitReport = () => {
    return mutateAsync({
      account_id: state.account_id,
      status_ids: state.status ? [state.status.id] : [],
      message_ids: state.chat_message?.id ? [state.chat_message?.id] : [],
      group_id: state.group?.id || null,
      rule_ids: state.rule_ids,
      comment: state.comment,
      forward: state.forward,
      status_version: status
        ? status?.reblog?.version || status?.version
        : undefined,
    });
  };

  const value = useMemo(() => {
    return {
      accountId: state.account_id,
      entityType: state.entityType,
      isBlocked: state.block,
      ruleIds: state.rule_ids,
      selectedStatus: state.status,
      selectedChatMessage: state.chat_message,
      selectedGroup: state.group,
      comment: state.comment,
      isSubmitting,
      changeReportBlock,
      changeReportComment,
      changeReportRule,
      submitReport,
    };
  }, [state]);

  return (
    <ReportsContext.Provider value={value}>{children}</ReportsContext.Provider>
  );
};

const useReportsContext = (): ReportsContextType => {
  const context = useContext(ReportsContext);
  if (!context) {
    throw new Error("useReportsContext must be used within a ReportsProvider");
  }
  return context;
};

export { ReportsProvider, useReportsContext, ReportableEntities };
